import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createTrip, extractFromUrl, addEvent, addReference, addStop } from '../lib/api';
import { WatercolorBloom } from '../components/WatercolorBloom';
import { IconArrowRight, IconSpark } from '../components/Icons';

export default function NewTrip() {
  const nav = useNavigate();
  const [tab, setTab] = useState<'paste' | 'manual'>('paste');
  return (
    <div className="col" style={{ gap: 24 }}>
      <section className="card brackets" style={{ position: 'relative', overflow: 'hidden', padding: 32 }}>
        <WatercolorBloom variant="duo" size={400} opacity={0.4} style={{ position: 'absolute', top: -140, right: -120 }} />
        <div style={{ position: 'relative' }}>
          <span className="label-bright">// SKETCH A NEW JOURNEY</span>
          <h1 className="h2" style={{ marginTop: 8 }}>Drop a link or start from scratch.</h1>
          <p className="body muted" style={{ maxWidth: 580, marginTop: 12 }}>
            Paste an event, conference, or itinerary URL — Atlas will read it and pre-fill the details. You confirm and ship.
          </p>
          <div className="tabs" style={{ marginTop: 18 }}>
            <button className={`tab ${tab === 'paste' ? 'active' : ''}`} onClick={() => setTab('paste')}>Paste link</button>
            <button className={`tab ${tab === 'manual' ? 'active' : ''}`} onClick={() => setTab('manual')}>Manual</button>
          </div>
        </div>
      </section>

      {tab === 'paste' ? <PasteFlow /> : <ManualFlow />}
    </div>
  );
}

function PasteFlow() {
  const nav = useNavigate();
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [draft, setDraft] = useState<any | null>(null);
  const [tripTitle, setTripTitle] = useState('');
  const [stopCity, setStopCity] = useState('');

  async function onExtract() {
    setBusy(true); setErr(null);
    try {
      const d = await extractFromUrl(url);
      setDraft(d);
      setTripTitle(d.title ?? 'Untitled trip');
      setStopCity(d.city ?? '');
    } catch (e: any) { setErr(e?.message ?? 'extraction failed'); }
    finally { setBusy(false); }
  }

  async function onCreate() {
    if (!draft) return;
    setBusy(true);
    try {
      const trip = await createTrip({
        title: tripTitle,
        status: 'researching',
        summary: draft.description ?? null,
        start_date: draft.start_date ?? null,
        end_date: draft.end_date ?? null,
        cover_color: 'electric_horizon',
      });
      if (stopCity) {
        await addStop({ trip_id: trip.id, city: stopCity, country: draft.country ?? null, start_date: draft.start_date ?? null, end_date: draft.end_date ?? null, sort_order: 0 });
      }
      await addEvent({
        trip_id: trip.id, title: draft.title ?? 'Event',
        description: draft.description ?? null,
        event_type: draft.event_type ?? 'other',
        start_at: draft.start_date ? new Date(draft.start_date).toISOString() : null,
        location_name: draft.location_name ?? null,
        source_url: url, source_payload: draft.raw ?? null,
      });
      await addReference({ trip_id: trip.id, url, title: draft.title ?? url });
      nav(`/trip/${trip.id}`);
    } catch (e: any) {
      setErr(e?.message ?? 'create failed');
    } finally { setBusy(false); }
  }

  return (
    <section className="card" style={{ padding: 24 }}>
      <div className="col" style={{ gap: 12 }}>
        <label className="label">URL TO EXTRACT</label>
        <div className="row" style={{ gap: 8 }}>
          <input className="input" placeholder="https://eoquebec.com/en/activite/glc-2027-in-japan/" value={url} onChange={e => setUrl(e.target.value)} />
          <button className="btn btn-primary" disabled={!url || busy} onClick={onExtract}>
            {busy ? 'Reading signal…' : <>Extract <IconArrowRight size={14} /></>}
          </button>
        </div>
        {err && <div className="label-bright" style={{ color: '#FF2BD6' }}>{err}</div>}
      </div>

      {draft && (
        <div style={{ marginTop: 24 }}>
          <div className="row" style={{ gap: 8, marginBottom: 12 }}>
            <IconSpark color="var(--c-lime)" />
            <span className="label-bright" style={{ color: 'var(--c-lime)' }}>// AI EXTRACTION COMPLETE</span>
          </div>
          <div className="col" style={{ gap: 12 }}>
            <Field label="Trip title"><input className="input" value={tripTitle} onChange={e => setTripTitle(e.target.value)} /></Field>
            <Field label="Primary stop (city)"><input className="input" value={stopCity} onChange={e => setStopCity(e.target.value)} /></Field>
            <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
              <Detail label="Suggested dates">{draft.start_date ?? '—'} → {draft.end_date ?? '—'}</Detail>
              <Detail label="Type">{(draft.event_type ?? 'other').toUpperCase()}</Detail>
              <Detail label="Country">{draft.country ?? '—'}</Detail>
              <Detail label="Location">{draft.location_name ?? '—'}</Detail>
            </div>
            {draft.description && <Field label="Summary"><textarea className="textarea" defaultValue={draft.description} onChange={e => (draft.description = e.target.value)} /></Field>}
            <button className="btn btn-primary" onClick={onCreate} disabled={busy}>Create trip <IconArrowRight size={14} /></button>
          </div>
        </div>
      )}
    </section>
  );
}

function ManualFlow() {
  const nav = useNavigate();
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [status, setStatus] = useState<'idea'|'researching'|'planning'|'booked'>('idea');
  const [busy, setBusy] = useState(false);

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const t = await createTrip({ title, summary: summary || null, status, start_date: start || null, end_date: end || null });
      nav(`/trip/${t.id}`);
    } finally { setBusy(false); }
  }

  return (
    <form className="card" style={{ padding: 24 }} onSubmit={onCreate}>
      <div className="col" style={{ gap: 12 }}>
        <Field label="Title"><input className="input" required value={title} onChange={e => setTitle(e.target.value)} placeholder="Japan + India, April 2027" /></Field>
        <Field label="Summary"><textarea className="textarea" value={summary} onChange={e => setSummary(e.target.value)} placeholder="Short sketch of what this trip is about…" /></Field>
        <div className="row" style={{ gap: 12 }}>
          <Field label="Start"><input className="input" type="date" value={start} onChange={e => setStart(e.target.value)} /></Field>
          <Field label="End"><input className="input" type="date" value={end} onChange={e => setEnd(e.target.value)} /></Field>
          <Field label="Status">
            <select className="select" value={status} onChange={e => setStatus(e.target.value as any)}>
              <option value="idea">Idea</option>
              <option value="researching">Researching</option>
              <option value="planning">Planning</option>
              <option value="booked">Booked</option>
            </select>
          </Field>
        </div>
        <button className="btn btn-primary" disabled={busy}>Create trip <IconArrowRight size={14} /></button>
      </div>
    </form>
  );
}

function Field({ label, children }: any) {
  return <label className="col" style={{ gap: 4 }}><span className="label">{label}</span>{children}</label>;
}
function Detail({ label, children }: any) {
  return <div className="col" style={{ gap: 2 }}><span className="label">{label}</span><span className="body">{children}</span></div>;
}
